import type { SearchResult } from './hubspot';

/**
 * Minimal record after connector normalization (mirror HubSpot list shape).
 * Raw API payloads may be flat (`Id`, `Name`, …); `normalizeSalesforceBrowseRecord`
 * maps those to `{ id, properties }`.
 */
export interface SalesforceBrowseRecord {
  id: string;
  properties: Record<string, any>;
  [key: string]: any;
}

export enum SalesforceTargetObjectName {
  ACCOUNT = 'Account',
  CONTACT = 'Contact',
  LEAD = 'Lead',
  OPPORTUNITY = 'Opportunity'
}

export interface ProspectFieldOption {
  key: string; // e.g. "prospect.domain" or "prospect.linkedin_url"
  label: string; // e.g. "Prospect Data — Domain"
}

export interface ProspectMappingOptionsResponse {
  target: string;
  prospect_sources: ProspectFieldOption[];
}

export enum NormalizeRule {
  WEBSITE = 'website',
  LINKEDIN = 'linkedin',
  NONE = 'none'
}

export enum FieldCategory {
  TEXT = 'text',
  DATE = 'date',
  DATETIME = 'datetime',
  PICKLIST = 'picklist',
  LOOKUP = 'lookup',
  NUMBER = 'number'
}

export enum LookupSearchBy {
  NAME = 'Name',
  ID = 'Id'
}

export const ALLOWED_FIELD_CATEGORIES = new Set([
  FieldCategory.TEXT,
  FieldCategory.DATE,
  FieldCategory.DATETIME,
  FieldCategory.PICKLIST,
  FieldCategory.LOOKUP,
  FieldCategory.NUMBER
]);

export enum SalesforceMappingStep {
  PRIMARY_KEY = 'primary_key',
  OTHER_FIELDS = 'other_fields'
}

export interface PicklistValue {
  value: string;
  label: string;
  active: boolean;
  defaultValue: boolean;
}

export interface SalesforceFieldForMapping {
  apiName: string;
  label: string;
  dataType: string;
  fieldCategory?: FieldCategory;
  picklistValues?: PicklistValue[];
  lookupSearchOptions?: string[];
  referenceTo?: string[];
  length?: number;
  precision?: number;
  scale?: number;
  isCreateable?: boolean;
  isUpdateable?: boolean;
  isFilterable?: boolean;
  isNillable?: boolean;
  isDefaultedOnCreate?: boolean;
  required?: boolean;
}

export interface MappingField {
  source_col: string;
  target_field_api_name: string;
  use_for_search: boolean;
  normalize: NormalizeRule;
  lookup_search_by?: LookupSearchBy;
  allow_overwrite?: boolean; // If true, field will be included in update payloads. If false, field will be preserved during updates. Always included for creates. Default: false
  order: number;
}

export interface MappingConfig {
  target_object: SalesforceTargetObjectName;
  fields: MappingField[];
}

/** Per-field validation counts returned on Salesforce mapped upsert preview. */
export interface SalesforceScalarFieldValidityCounts {
  valid: number;
  invalid: number;
  /** When true, Salesforce will not persist this column; spreadsheet values may still be used for search/matching. */
  never_valid_for_sync_writes?: boolean;
}

export interface SalesforceLookupFieldValidityCounts {
  exact_match: number;
  partial: number;
  no_match: number;
  never_valid_for_sync_writes?: boolean;
}

export interface ValidationSummary {
  date_fields?: Record<string, SalesforceScalarFieldValidityCounts>;
  datetime_fields?: Record<string, SalesforceScalarFieldValidityCounts>;
  picklist_fields?: Record<string, SalesforceScalarFieldValidityCounts>;
  email_fields?: Record<string, SalesforceScalarFieldValidityCounts>;
  phone_fields?: Record<string, SalesforceScalarFieldValidityCounts>;
  number_fields?: Record<string, SalesforceScalarFieldValidityCounts>;
  text_fields?: Record<string, SalesforceScalarFieldValidityCounts>;
  lookup_fields?: Record<string, SalesforceLookupFieldValidityCounts>;
}

export interface DuplicateLookupWarning {
  lookup_field: string; // Salesforce field API name (e.g., "AccountId")
  duplicate_value: string; // The duplicate name/ID that was found (e.g., "Acme Corp")
  lookup_table: string; // The referenced object (e.g., "Account")
}

export interface PrimaryKeyMapping {
  source_column: string;
  target_field: string;
}

export interface LookupSuggestion {
  id: string; // Salesforce record ID (e.g., "001XX000004ABCD")
  name: string; // Display name (e.g., "Exxon Mobil")
  exact_match: boolean; // true if exact match (case-insensitive)
}

/**
 * Primary-key / uniqueness conflicts from Salesforce mapped upsert preview
 * (`previewSalesforceMappedUpsert`). Same wire shape as {@link HubSpotSearchResponse}; split for CRM-specific typing.
 */
export interface SalesforcePreviewConflictResponse {
  results: {
    [normalized_key: string]: SearchResult[];
  };
  not_found: string[];
}

export interface FieldErrorCounts {
  missing: number; // Count of rows where this field was missing/empty
  invalid: number; // Count of rows where this field had an invalid value
}

export interface RequiredFieldValidation {
  creates?: {
    valid_for_creation: number; // Count of valid creates (will proceed)
    invalid_for_creation: number; // Count of invalid creates (entire row will be dropped)
    field_errors: Record<string, FieldErrorCounts>; // Grouped by field with counts
  };
  updates?: {
    valid_for_overwriting: number; // Count of valid updates (will proceed)
    invalid_for_overwriting: number; // Count of invalid updates (entire row will be dropped)
    skipped_no_overwrite: number; // Count of updates skipped (all allow_overwrite=False)
    field_errors: Record<string, FieldErrorCounts>; // Grouped by field with counts
  };
}

/** Rows sharing the same normalized primary key in the spreadsheet batch sent to preview. */
export interface SalesforceInputDuplicateGroup {
  normalized_primary_key: string;
  display_label: string;
  /** 0-based indices into the `rows` array passed to preview / sync prep. */
  row_indices: number[];
}

export interface UpsertMappedPreviewResponse {
  total_records: number;
  records_to_update: number;
  records_to_create: number;
  records_skipped: number;
  search_strategy: string;
  validation_summary?: ValidationSummary;
  validation?: RequiredFieldValidation; // New required field validation
  can_proceed?: boolean; // Whether sync can proceed (false if ALL creates are invalid)
  primary_key_mapping?: PrimaryKeyMapping;
  warnings?: DuplicateLookupWarning[];
  results: SalesforcePreviewConflictResponse['results'];
  not_found: SalesforcePreviewConflictResponse['not_found'];
  /** Duplicate spreadsheet rows detected in the preview payload (same normalized PK). */
  has_input_duplicates?: boolean;
  input_duplicate_groups?: SalesforceInputDuplicateGroup[] | null;
  /**
   * Per lookup field (e.g. `ParentId`) → per spreadsheet source value (`source_col` cell text)
   * → suggested Salesforce records for that ambiguity. Empty array means **no candidates** from the
   * backend (distinct from ambiguous multiple matches — those are arrays with one or more items).
   * Example: `{ "ParentId": { "Acme Parent": [{ id, name, exact_match }] } }` — or same key with `[]`.
   */
  lookup_suggestions?: {
    [target_field_api_name: string]: {
      [source_cell_value: string]: LookupSuggestion[];
    };
  } | null;
  company_association_suggestions?: unknown | null;
}

export interface UserConfirmedLookups {
  [target_field_api_name: string]: {
    [source_value: string]: string | null;
  };
}

/** True when at least one lookup/source pair has at least one suggestion to pick from. */
export function hasLookupSuggestionChoices(
  lookup_suggestions:
    | UpsertMappedPreviewResponse['lookup_suggestions']
    | null
    | undefined
): boolean {
  if (!lookup_suggestions) return false;
  return Object.values(lookup_suggestions).some(
    (byValue) =>
      byValue != null &&
      Object.values(byValue).some(
        (list) => Array.isArray(list) && list.length > 0
      )
  );
}
